import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { isValidPinnedTile, type PinnedTile, type TileSource, type TileSpec } from './PinnedTileTypes';

const STORAGE_KEY = 'ut-pinned-tiles';

function readStored(): PinnedTile[] {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(isValidPinnedTile) : [];
    } catch {
        return [];
    }
}

function writeStored(tiles: PinnedTile[]): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tiles));
}

function makeId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `pinned-${crypto.randomUUID()}`;
    }
    return `pinned-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

/**
 * Holds the user's custom (pinned) tiles, persisted to localStorage so they
 * survive reloads — the same `readStored`/`writeStored` pattern as
 * {@link ThemeService}, the app's one existing persistence mechanism. Registered
 * as a DI singleton so the AMA "pin" action, the "Add insight" modal, and every
 * surface's gallery share one source of truth and re-render together.
 */
@injectable()
export class PinnedTilesStore extends BaseViewModel {
    private tiles: PinnedTile[] = readStored();

    /** The tiles pinned to a given surface, oldest first. */
    tilesFor(surfaceId: string): PinnedTile[] {
        return this.tiles.filter((tile) => tile.surfaceId === surfaceId);
    }

    /** Creates and persists a tile for a surface, returning the stored tile. */
    pin(surfaceId: string, spec: TileSpec, source: TileSource): PinnedTile {
        const tile: PinnedTile = { id: makeId(), surfaceId, spec, source, savedAt: new Date().toISOString() };
        this.tiles = [...this.tiles, tile];
        this.persist();
        return tile;
    }

    /** Replaces the spec of an existing tile, preserving its id/source/savedAt (no-op if absent). */
    update(id: string, spec: TileSpec): void {
        let changed = false;
        const next = this.tiles.map((tile) => {
            if (tile.id !== id) return tile;
            changed = true;
            return { ...tile, spec };
        });
        if (!changed) return;
        this.tiles = next;
        this.persist();
    }

    /** Removes a tile by id (no-op if absent). */
    remove(id: string): void {
        const next = this.tiles.filter((tile) => tile.id !== id);
        if (next.length === this.tiles.length) return;
        this.tiles = next;
        this.persist();
    }

    private persist(): void {
        writeStored(this.tiles);
        this.notifyChange();
    }
}
